/**
 * Nikihashi Theme — theme.js
 * Vanilla JavaScript. No jQuery. No heavy libraries.
 *
 * Modules:
 *  1. Utility helpers
 *  2. CartAPI — wraps Shopify Cart AJAX API
 *  3. CartDrawer — slide-in cart UI
 *  4. VariantSelector — variant switching (price/availability/URL/image)
 *  5. ImageGallery — product image gallery with thumbnails
 *  6. QuantityStepper — + / - buttons for quantity inputs
 *  7. MobileMenu — hamburger menu drawer
 *  8. SearchModal — search overlay + predictive search
 *  9. ScrollEffects — header scroll transparency
 * 10. ProductRecommendations — loads Shopify recs via API
 * 11. QuickAdd — product card quick-add-to-cart
 * 12. CollectionFilter — sort/filter form auto-submit
 * 13. Init — bootstraps all modules
 */

'use strict';

/* ============================================================
   1. UTILITY HELPERS
   ============================================================ */

/** Query selector shorthand */
const $ = (selector, context = document) => context.querySelector(selector);
const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

/** Dispatch a custom event on an element */
function dispatch(el, name, detail = {}) {
  el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

/** Format money from Shopify (integer cents) to display string */
function formatMoney(cents) {
  // Use Shopify's money format if available, else fallback
  if (window.Shopify && window.Shopify.money_format) {
    const formatted = (cents / 100).toFixed(2);
    return window.Shopify.money_format.replace('{{amount}}', formatted)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }
  return '₹' + (cents / 100).toFixed(2);
}

/** Debounce function */
function debounce(fn, wait) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/** Trap focus inside an element (for accessibility) */
function trapFocus(el) {
  const focusable = $$('a[href], button:not([disabled]), input, textarea, select, details, [tabindex]:not([tabindex="-1"])', el);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  el.addEventListener('keydown', handleKeydown);
  return () => el.removeEventListener('keydown', handleKeydown);
}

/** Show/hide overlay */
function showOverlay() {
  const overlay = $('#overlay');
  if (overlay) { overlay.classList.add('is-visible'); document.body.classList.add('drawer-open'); }
}

function hideOverlay() {
  const overlay = $('#overlay');
  if (overlay) { overlay.classList.remove('is-visible'); document.body.classList.remove('drawer-open'); }
}

/* ============================================================
   2. CART API
   Wraps Shopify's AJAX Cart API with fetch().
   All cart operations go through here — keeping Shopify as truth.
   ============================================================ */
const CartAPI = {
  /** Fetch current cart state */
  getCart() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json());
  },

  /**
   * Add item to cart
   * @param {number} variantId - Shopify variant ID
   * @param {number} quantity
   * @param {object} properties - line item properties
   */
  addItem(variantId, quantity = 1, properties = {}) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    }).then(r => {
      if (!r.ok) return r.json().then(err => Promise.reject(err));
      return r.json();
    });
  },

  /**
   * Change quantity of a line item by key
   * @param {string} key - line item key
   * @param {number} quantity
   */
  changeItem(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    }).then(r => r.json());
  },

  /** Remove a line item */
  removeItem(key) {
    return this.changeItem(key, 0);
  },

  /** Update cart (bulk) */
  updateCart(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    }).then(r => r.json());
  },

  /** Clear all items */
  clearCart() {
    return fetch('/cart/clear.js', { method: 'POST' }).then(r => r.json());
  }
};

/* ============================================================
   3. CART DRAWER
   ============================================================ */
class CartDrawer {
  constructor() {
    this.drawer = $('#CartDrawer');
    if (!this.drawer) return;

    this.body = $('#CartDrawerBody');
    this.footer = $('#CartDrawerFooter');
    this.countEl = $('#CartDrawerCount');
    this.badgeEl = $('#CartBadge');
    this.isOpen = false;
    this.removeTrapFocus = null;

    this.bindEvents();
  }

  bindEvents() {
    // Open triggers
    $$('[data-cart-trigger]').forEach(el => {
      el.addEventListener('click', () => this.open());
    });

    // Close triggers
    this.drawer.addEventListener('click', e => {
      if (e.target.closest('[data-cart-close]') || e.target === this.drawer) {
        this.close();
      }
    });

    // Overlay click
    $('#overlay')?.addEventListener('click', () => {
      if (this.isOpen) this.close();
    });

    // Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Quantity changes (delegated)
    this.drawer.addEventListener('click', e => {
      const minusBtn = e.target.closest('[data-cart-qty-minus]');
      const plusBtn  = e.target.closest('[data-cart-qty-plus]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (minusBtn) this.changeQty(minusBtn.dataset.cartQtyMinus, -1);
      if (plusBtn)  this.changeQty(plusBtn.dataset.cartQtyPlus, 1);
      if (removeBtn) this.removeItem(removeBtn.dataset.cartRemove);
    });

    // Input change (delegated)
    this.drawer.addEventListener('change', e => {
      const input = e.target.closest('[data-cart-qty-input]');
      if (input) {
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        CartAPI.changeItem(input.dataset.cartQtyInput, qty).then(cart => this.refresh(cart));
      }
    });

    // Cart page quantity changes
    document.addEventListener('click', e => {
      const minusBtn  = e.target.closest('[data-cart-qty-minus]');
      const plusBtn   = e.target.closest('[data-cart-qty-plus]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (!minusBtn && !plusBtn && !removeBtn) return;

      // Not in drawer — check for cart page
      if (!e.target.closest('#CartDrawer')) {
        if (minusBtn) this.changeQtyPage(minusBtn.dataset.cartQtyMinus, -1);
        if (plusBtn)  this.changeQtyPage(plusBtn.dataset.cartQtyPlus, 1);
        if (removeBtn) this.removeItemPage(removeBtn.dataset.cartRemove);
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    showOverlay();
    this.isOpen = true;
    $('#CartToggle')?.setAttribute('aria-expanded', 'true');
    this.removeTrapFocus = trapFocus(this.drawer);

    // Focus close button
    setTimeout(() => $('#CartDrawerClose')?.focus(), 50);
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    hideOverlay();
    this.isOpen = false;
    $('#CartToggle')?.setAttribute('aria-expanded', 'false');
    if (this.removeTrapFocus) { this.removeTrapFocus(); this.removeTrapFocus = null; }
    $('#CartToggle')?.focus();
  }

  /** Refresh drawer content after a cart change */
  refresh(cart) {
    this.updateBadge(cart.item_count);
    this.renderItems(cart);
    this.renderFooter(cart);
  }

  updateBadge(count) {
    if (!this.badgeEl) return;
    this.badgeEl.textContent = count;
    this.badgeEl.classList.toggle('is-hidden', count === 0);
    this.badgeEl.classList.add('bump');
    setTimeout(() => this.badgeEl.classList.remove('bump'), 300);

    if (this.countEl) this.countEl.textContent = `(${count})`;

    // Also update cart page count if present
    const cartTitle = document.querySelector('.main-cart .page-title .page-title__count');
    if (cartTitle) cartTitle.textContent = `(${count})`;
  }

  renderItems(cart) {
    if (!this.body) return;

    if (cart.item_count === 0) {
      this.body.innerHTML = this.emptyHTML();
      return;
    }

    const itemsHtml = cart.items.map((item, index) => this.itemHTML(item, index)).join('');
    this.body.innerHTML = `<ul class="cart-items" id="CartItems" role="list" aria-label="Cart items">${itemsHtml}</ul>`;
  }

  itemHTML(item, index) {
    const variantInfo = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__variant">${item.variant_title}</p>`
      : '';

    const priceHtml = item.original_line_price !== item.final_line_price
      ? `<s class="price--compare">${formatMoney(item.original_line_price)}</s>
         <span class="price--current">${formatMoney(item.final_line_price)}</span>`
      : formatMoney(item.final_line_price);

    const imgHtml = item.image
      ? `<img src="${item.image}" alt="${this.escapeHtml(item.product_title)}" width="70" height="70" loading="lazy" class="cart-item__img">`
      : '';

    return `
      <li class="cart-item" data-key="${item.key}" id="CartItem-${index}">
        <a href="${item.url}" class="cart-item__img-link" tabindex="-1" aria-hidden="true">
          ${imgHtml}
        </a>
        <div class="cart-item__info">
          <div class="cart-item__top">
            <div>
              <a href="${item.url}" class="cart-item__title">${this.escapeHtml(item.product_title)}</a>
              ${variantInfo}
            </div>
            <button type="button" class="btn-icon cart-item__remove"
              aria-label="Remove ${this.escapeHtml(item.product_title)}"
              data-cart-remove="${item.key}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" width="16" height="16"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
            </button>
          </div>
          <div class="cart-item__bottom">
            <div class="cart-item__qty quantity-wrapper quantity-wrapper--sm">
              <button type="button" class="quantity-btn quantity-btn--minus" aria-label="Decrease quantity" data-cart-qty-minus="${item.key}">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" width="16" height="16"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <input type="number" value="${item.quantity}" min="0" class="quantity-input" aria-label="Quantity" data-cart-qty-input="${item.key}">
              <button type="button" class="quantity-btn quantity-btn--plus" aria-label="Increase quantity" data-cart-qty-plus="${item.key}">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
            </div>
            <span class="cart-item__price">${priceHtml}</span>
          </div>
        </div>
      </li>`;
  }

  renderFooter(cart) {
    if (!this.footer) return;

    if (cart.item_count === 0) {
      this.footer.innerHTML = '';
      this.footer.hidden = true;
      return;
    }

    this.footer.hidden = false;

    const discountsHtml = cart.cart_level_discount_applications.length > 0
      ? `<div class="cart-drawer__discounts">
          ${cart.cart_level_discount_applications.map(d =>
            `<div class="cart-discount"><span>${this.escapeHtml(d.title)}</span><span>-${formatMoney(d.total_allocated_amount)}</span></div>`
          ).join('')}
        </div>`
      : '';

    this.footer.innerHTML = `
      ${discountsHtml}
      <div class="cart-drawer__subtotal">
        <span>Subtotal</span>
        <span class="cart-drawer__subtotal-price" id="CartSubtotal">${formatMoney(cart.total_price)}</span>
      </div>
      <p class="cart-drawer__note">Taxes and shipping calculated at checkout.</p>
      <a href="/checkout" class="btn btn--pixel btn--full btn--lg cart-drawer__checkout" id="CartCheckoutBtn">
        Checkout — <span id="CartCheckoutPrice">${formatMoney(cart.total_price)}</span>
      </a>
      <button type="button" class="cart-drawer__continue" data-cart-close>
        Continue Shopping
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" width="14" height="14"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </button>`;
  }

  emptyHTML() {
    return `
      <div class="cart-drawer__empty">
        <div class="cart-drawer__empty-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" width="64" height="64"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg></div>
        <p class="cart-drawer__empty-title">Your cart is empty</p>
        <p class="cart-drawer__empty-text">Looks like you haven't added anything yet.</p>
        <a href="/collections/all" class="btn btn--pixel btn--outline" data-cart-close>Browse Products</a>
      </div>`;
  }

  changeQty(key, delta) {
    const item = this.drawer.querySelector(`[data-key="${key}"]`);
    const input = item?.querySelector('[data-cart-qty-input]');
    const currentQty = input ? parseInt(input.value, 10) : 1;
    const newQty = Math.max(0, currentQty + delta);
    CartAPI.changeItem(key, newQty).then(cart => this.refresh(cart));
  }

  removeItem(key) {
    CartAPI.removeItem(key).then(cart => this.refresh(cart));
  }

  changeQtyPage(key, delta) {
    const item = document.querySelector(`[data-key="${key}"]`);
    const input = item?.querySelector('[data-cart-qty-input]');
    const currentQty = input ? parseInt(input.value, 10) : 1;
    const newQty = Math.max(0, currentQty + delta);
    if (input) input.value = newQty;
    CartAPI.changeItem(key, newQty).then(cart => {
      this.updateBadge(cart.item_count);
      this.updateCartPageSubtotal(cart.total_price);
    });
  }

  removeItemPage(key) {
    CartAPI.removeItem(key).then(cart => {
      const item = document.querySelector(`[data-key="${key}"]`);
      if (item) item.remove();
      this.updateBadge(cart.item_count);
      this.updateCartPageSubtotal(cart.total_price);
      if (cart.item_count === 0) location.reload();
    });
  }

  updateCartPageSubtotal(price) {
    const el = $('#CartPageSubtotal');
    if (el) el.textContent = formatMoney(price);
    const checkoutBtn = $('#CartPageCheckout');
    if (checkoutBtn) checkoutBtn.textContent = `Checkout — ${formatMoney(price)}`;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

/* ============================================================
   4. VARIANT SELECTOR
   Reads product.variants JSON and updates price, availability,
   image, and URL when a variant option is changed.
   ============================================================ */
class VariantSelector {
  constructor(formWrapper) {
    this.wrapper = formWrapper;
    this.productId = formWrapper.dataset.productId;
    this.sectionId = formWrapper.dataset.sectionId;

    // Parse variants JSON embedded by product-form.liquid
    const variantsEl = document.getElementById(formWrapper.dataset.variantsId);
    if (!variantsEl) return;

    this.variants = JSON.parse(variantsEl.textContent);
    this.currentOptions = this.getSelectedOptions();
    this.currentVariant = this.findVariant(this.currentOptions);

    this.bindEvents();
  }

  bindEvents() {
    // Listen on the wrapper for any radio change
    this.wrapper.addEventListener('change', e => {
      const input = e.target;
      // Only act on our option radio inputs
      if (!input.hasAttribute('data-option-index')) return;

      this.currentOptions = this.getSelectedOptions();
      const newVariant = this.findVariant(this.currentOptions);

      if (newVariant) {
        this.updateForm(newVariant);
        this.updatePrice(newVariant);
        this.updateGallery(newVariant);
        this.updateURL(newVariant);
        this.currentVariant = newVariant;
      }

      // Update the text label showing the selected option (e.g. "— M")
      const optionIndex = parseInt(input.dataset.optionIndex, 10);
      const selectedLabel = document.getElementById(`OptionSelected-${this.sectionId}-${optionIndex}`);
      if (selectedLabel) selectedLabel.textContent = `— ${input.value}`;

      // Toggle the is-active class on the parent label (.size-btn or .swatch)
      const parentGroup = input.closest('.product-form__buttons, .product-form__swatches');
      if (parentGroup) {
        // Remove active from all sibling labels in this group
        parentGroup.querySelectorAll('label.size-btn, label.swatch').forEach(lbl => {
          lbl.classList.remove('is-active');
        });
        // Add active to the label wrapping this input
        const parentLabel = input.closest('label.size-btn, label.swatch');
        if (parentLabel) parentLabel.classList.add('is-active');
      }
    });
  }

  getSelectedOptions() {
    // Build the options array indexed by option position
    // Use a plain object first, then convert to ordered array
    const optionMap = {};
    const inputs = this.wrapper.querySelectorAll('input[data-option-index]');
    inputs.forEach(input => {
      if (input.type === 'radio' && !input.checked) return;
      const idx = parseInt(input.dataset.optionIndex, 10);
      optionMap[idx] = input.value;
    });
    // Convert map to array preserving order
    const maxIndex = Math.max(...Object.keys(optionMap).map(Number));
    const options = [];
    for (let i = 0; i <= maxIndex; i++) {
      options.push(optionMap[i] || null);
    }
    return options;
  }

  findVariant(options) {
    return this.variants.find(v =>
      v.options.every((opt, i) => options[i] == null || opt === options[i])
    ) || null;
  }

  updateForm(variant) {
    // Update hidden variant ID input
    const idInput = document.getElementById(`ProductVariantId-${this.sectionId}`);
    if (idInput) idInput.value = variant.id;

    // Update add to cart button state
    const submitBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    const btnText   = document.getElementById(`AddToCartText-${this.sectionId}`);

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      submitBtn.setAttribute('aria-disabled', !variant.available);
    }
    if (btnText) {
      btnText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  updatePrice(variant) {
    const priceEl = document.getElementById(`ProductPrice-${this.sectionId}`);
    if (!priceEl) return;

    const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    const savingsPct = isOnSale
      ? Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100)
      : 0;

    let html = '';

    if (!variant.available) {
      html = `<div class="price-wrapper price-wrapper--sold-out">
        <span class="price-label price-label--sold-out">Sold Out</span>
      </div>`;
    } else {
      html = `<div class="price-wrapper${isOnSale ? ' price-wrapper--sale' : ''}">
        <span class="price price--current">${formatMoney(variant.price)}</span>
        ${isOnSale ? `<span class="price price--compare"><s>${formatMoney(variant.compare_at_price)}</s></span>
          <span class="badge badge--sale">-${savingsPct}%</span>` : ''}
      </div>`;
    }

    priceEl.innerHTML = html;
  }

  updateGallery(variant) {
    if (!variant.featured_image && !variant.featured_media) return;

    // Use featured_media id or featured_image id
    const mediaId = variant.featured_media ? variant.featured_media.id : variant.featured_image.id;
    
    // Find slide that shows this variant's image
    const variantImgEl = document.querySelector(`[data-media-id="${mediaId}"]`);
    if (variantImgEl) {
      const index = parseInt(variantImgEl.dataset.slideIndex, 10);
      
      // Update global image gallery if it exists
      if (window.productGallery) {
        window.productGallery.goTo(index);
      } else {
        this.switchGallerySlide(index);
      }
    }
  }

  switchGallerySlide(index) {
    const gallery = document.getElementById('ProductGallery-' + this.productId);
    if (!gallery) return;

    gallery.querySelectorAll('.product-gallery__slide').forEach((s, i) => {
      s.classList.toggle('is-active', i === index);
    });

    gallery.querySelectorAll('.gallery-thumb').forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  updateURL(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
}

/* ============================================================
   5. IMAGE GALLERY
   ============================================================ */
class ImageGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.slides = $$('.product-gallery__slide', gallery);
    this.thumbs = $$('.gallery-thumb', gallery);
    this.prevBtn = $('.gallery-nav--prev', gallery);
    this.nextBtn = $('.gallery-nav--next', gallery);
    this.currentIndex = 0;

    if (!this.slides.length) return;
    this.bindEvents();
  }

  bindEvents() {
    // Thumbnail clicks
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
    });

    // Arrow nav
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    // Touch swipe support
    let touchStartX = 0;
    this.gallery.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    this.gallery.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? this.goTo(this.currentIndex + 1) : this.goTo(this.currentIndex - 1); }
    });
  }

  goTo(index) {
    const total = this.slides.length;
    // Clamp — no wrapping on product gallery
    this.currentIndex = Math.max(0, Math.min(index, total - 1));

    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.currentIndex));
    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === this.currentIndex);
      t.setAttribute('aria-selected', i === this.currentIndex ? 'true' : 'false');
    });
  }
}

/* ============================================================
   6. QUANTITY STEPPER
   Handles + / - buttons on product page (not in cart drawer;
   those are handled by CartDrawer).
   ============================================================ */
function initQuantitySteppers() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-quantity-action]');
    if (!btn) return;

    const wrapper = btn.closest('.quantity-wrapper') || btn.closest('.product-form__quantity');
    if (!wrapper) return;

    const input = wrapper.querySelector('.quantity-input');
    if (!input) return;

    // Skip cart-managed steppers
    if (btn.dataset.cartQtyMinus || btn.dataset.cartQtyPlus) return;

    const current = parseInt(input.value, 10) || 1;
    const action = btn.dataset.quantityAction;
    const min = parseInt(input.min, 10) || 1;

    if (action === 'plus') {
      input.value = current + 1;
    } else if (action === 'minus' && current > min) {
      input.value = current - 1;
    }
  });
}

/* ============================================================
   7. ADD TO CART (product page form)
   ============================================================ */
function initAddToCart() {
  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-type="add-to-cart-form"]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[data-add-to-cart]');
    const variantIdInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');
    const errorEl = form.querySelector('[id^="CartError-"]');

    if (!variantIdInput || !btn) return;

    const variantId = parseInt(variantIdInput.value, 10);
    const quantity = parseInt(qtyInput?.value, 10) || 1;

    // Loading state
    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      await CartAPI.addItem(variantId, quantity);
      const cart = await CartAPI.getCart();

      // Update drawer
      if (window.cartDrawer) {
        window.cartDrawer.refresh(cart);
        window.cartDrawer.open();
      }

      if (errorEl) errorEl.classList.remove('is-visible');
    } catch (err) {
      const message = err?.description || err?.message || 'Could not add item to cart.';
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('is-visible');
      }
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  });
}

/* ============================================================
   8. QUICK ADD (product card)
   ============================================================ */
function initQuickAdd() {
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;

    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      await CartAPI.addItem(variantId, 1);
      const cart = await CartAPI.getCart();

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.querySelector('.btn-text').textContent = '✓ Added!';

      if (window.cartDrawer) {
        window.cartDrawer.refresh(cart);
        window.cartDrawer.open();
      }

      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.querySelector('.btn-text').textContent = 'Add to Cart';
        btn.disabled = false;
      }, 2000);
    } catch {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  });
}

/* ============================================================
   9. MOBILE MENU
   ============================================================ */
class MobileMenu {
  constructor() {
    this.menu = $('#MobileMenuDrawer');
    this.toggleBtn = $('#MobileMenuToggle');
    this.closeBtn = $('#MobileMenuClose');
    this.isOpen = false;

    if (!this.menu || !this.toggleBtn) return;
    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtn.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    $('#overlay')?.addEventListener('click', () => { if (this.isOpen) this.close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && this.isOpen) this.close(); });
  }

  open() {
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    showOverlay();
    this.isOpen = true;
    this.removeFocusTrap = trapFocus(this.menu);
    setTimeout(() => this.closeBtn?.focus(), 50);
  }

  close() {
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    hideOverlay();
    this.isOpen = false;
    if (this.removeFocusTrap) this.removeFocusTrap();
    this.toggleBtn.focus();
  }
}

/* ============================================================
   10. SEARCH MODAL
   ============================================================ */
class SearchModal {
  constructor() {
    this.modal = $('#SearchModal');
    this.toggleBtn = $('#SearchToggle');
    this.closeBtn = $('#SearchModalClose');
    this.input = $('#SearchInput');
    this.resultsEl = $('#SearchResults');
    this.isOpen = false;

    if (!this.modal) return;
    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && this.isOpen) this.close(); });
    $('#overlay')?.addEventListener('click', () => { if (this.isOpen) this.close(); });

    // Predictive search
    this.input?.addEventListener('input', debounce(e => {
      this.search(e.target.value.trim());
    }, 300));
  }

  open() {
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    document.body.classList.add('modal-open');
    
    // Explicitly hide the global overlay in case of caching issues
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.classList.remove('is-visible');

    setTimeout(() => this.input?.focus(), 50);
    
    // Listen for clicks outside to close
    this._outsideClickHandler = this._outsideClickHandler || ((e) => {
      if (this.isOpen && !this.modal.contains(e.target) && !this.toggleBtn.contains(e.target)) {
        this.close();
      }
    });
    document.addEventListener('click', this._outsideClickHandler);
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    document.body.classList.remove('modal-open');
    if (this.resultsEl) this.resultsEl.innerHTML = '';
    this.toggleBtn?.focus();
    
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
    }
  }

  async search(query) {
    if (!this.resultsEl || query.length < 2) {
      if (this.resultsEl) this.resultsEl.innerHTML = '';
      return;
    }

    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title`
      );
      const data = await res.json();
      const products = data?.resources?.results?.products || [];

      if (!products.length) {
        this.resultsEl.innerHTML = `<p style="color:var(--color-text-muted);font-size:0.875rem;padding:1rem;">No results for "${query}"</p>`;
        return;
      }

      this.resultsEl.innerHTML = products.map(p => `
        <a href="${p.url}" class="predictive-result">
          ${p.featured_image?.url
            ? `<img src="${p.featured_image.url}&width=120" alt="${p.title}" width="60" height="60" class="predictive-result__img">`
            : '<div style="width:60px;height:60px;background:var(--color-surface-2);border-radius:4px;"></div>'}
          <span class="predictive-result__title">${p.title}</span>
          <span class="predictive-result__price">${p.price ? formatMoney(p.price) : ''}</span>
        </a>`
      ).join('');
    } catch {
      // Fail silently — search still works on submit
    }
  }
}

/* ============================================================
   11. SCROLL EFFECTS
   ============================================================ */
function initScrollEffects() {
  const header = $('#SiteHeader');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 40);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll(); // Initial check
}

/* ============================================================
   12. PRODUCT RECOMMENDATIONS
   Uses Shopify's Product Recommendations API.
   ============================================================ */
function initProductRecommendations() {
  const container = $('#ProductRecommendations');
  if (!container) return;

  const productId = container.dataset.productId;
  const limit = container.dataset.limit || 4;

  if (!productId) return;

  fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`)
    .then(r => r.json())
    .then(data => {
      const products = data.products || [];
      const grid = $('#ProductRecsGrid');
      if (!grid || !products.length) {
        container.style.display = 'none';
        return;
      }

      // Render lightweight product cards from API data
      grid.innerHTML = products.map(p => {
        const imgSrc = p.featured_image
          ? `${p.featured_image}&width=600`
          : '';
        const price = formatMoney(p.price_min);
        const compareAt = p.compare_at_price_min && p.compare_at_price_min > p.price_min
          ? `<s class="price--compare">${formatMoney(p.compare_at_price_min)}</s>` : '';

        return `
          <div class="product-card pixel-card">
            <a href="${p.url}" class="product-card__media" aria-label="${p.title}">
              <div class="product-card__img-wrap">
                ${imgSrc ? `<img src="${imgSrc}" alt="${p.title}" loading="lazy" class="product-card__img product-card__img--primary">` : ''}
              </div>
            </a>
            <div class="product-card__info">
              <h3 class="product-card__title"><a href="${p.url}">${p.title}</a></h3>
              <div class="product-card__price">
                <div class="price-wrapper">
                  <span class="price price--current">${price}</span>
                  ${compareAt}
                </div>
              </div>
            </div>
          </div>`;
      }).join('');
    })
    .catch(() => {
      const container = $('#ProductRecommendations');
      if (container) container.style.display = 'none';
    });
}

/* ============================================================
   13. COLLECTION SORT (auto-submit on change)
   ============================================================ */
function initCollectionSort() {
  const sortSelect = $('#SortSelect');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });
}

/* ============================================================
   14. COLLECTION FILTER TOGGLE
   ============================================================ */
function initFilterToggle() {
  const filterToggle = $('#FilterToggle');
  const filterPanel = $('#FilterPanel');
  if (!filterToggle || !filterPanel) return;

  filterToggle.addEventListener('click', () => {
    const isOpen = filterPanel.classList.toggle('is-open');
    filterToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });

  // Auto-submit filter form on checkbox change
  const filterForm = $('#FilterForm');
  if (filterForm) {
    filterForm.addEventListener('change', debounce(() => filterForm.submit(), 400));
  }
}

/* ============================================================
   15. INIT — Bootstrap all modules on DOMContentLoaded
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {

  // Cart Drawer (global, used by many modules)
  window.cartDrawer = new CartDrawer();

  // Mobile Menu
  new MobileMenu();

  // Search Modal
  new SearchModal();

  // Scroll effects
  initScrollEffects();

  // Product page
  $$('[data-variants-id]').forEach(wrapper => {
    new VariantSelector(wrapper);
  });

  // Image gallery
  $$('[id^="ProductGallery-"]').forEach(gallery => {
    window.productGallery = new ImageGallery(gallery);
  });

  // Quantity steppers (product form)
  initQuantitySteppers();

  // Add to cart form
  initAddToCart();

  // Quick add (product cards)
  initQuickAdd();

  // Product recommendations
  initProductRecommendations();

  // Collection sort
  initCollectionSort();

  // Collection filter toggle
  initFilterToggle();

  // Animate product cards on scroll (IntersectionObserver)
  if ('IntersectionObserver' in window) {
    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          cardObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.1 });

    $$('.product-card').forEach(card => cardObserver.observe(card));
  }

});
